import { RefObject, ComponentProps, useState } from 'react';
import { NO_DATA_PLACEHOLDER } from '@sib/config';
import { createUseAppTranslation } from '@sib/i18n';
import {
  Button,
  cn,
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
  Input,
  Loader,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@sib/ui';
import { ChevronDown, PlusCircle } from '@sib/ui/icons';
import { TagItem } from './DocumentTagItem';
import {
  DocumentTagCategory,
  TagSelectorAction,
  TagErrorEnum,
} from '../document-tag-selector.enums';

const useDocumentsTranslation = createUseAppTranslation('documents');

interface Tag {
  uuid: string;
  name: string;
  isChecked: boolean;
  isPredefined?: boolean;
}

export type TagSelectorVariant = 'default' | 'form';

export interface TagSelectorProps {
  tags: Tag[];
  customTags: Tag[];
  predefinedTags: Tag[];
  tagLabel: string | (string | number)[];
  toggleTag: (tagId: string) => void;
  newTagName: string;
  handleTagNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  addNewTag: () => void;
  tagError: TagErrorEnum | null;
  tagErrorEnum: typeof TagErrorEnum;
  createTagPending: boolean;
  commandListRef: RefObject<HTMLDivElement | null>;
  disabled?: boolean;
  className?: string;
  editable?: boolean;
  variant?: TagSelectorVariant;
  PopoverTriggerProps?: ComponentProps<typeof PopoverTrigger>;
  triggerDataTestId?: string;
  addTagButtonDataTestId?: string;
}

export const TagSelector = ({
  tags,
  customTags,
  predefinedTags,
  tagLabel,
  toggleTag,
  newTagName,
  handleTagNameChange,
  handleKeyDown,
  addNewTag,
  tagError,
  tagErrorEnum,
  createTagPending,
  commandListRef,
  disabled,
  className,
  PopoverTriggerProps,
  editable = true,
  variant = 'default',
  triggerDataTestId,
  addTagButtonDataTestId,
}: TagSelectorProps) => {
  const { t } = useDocumentsTranslation();
  const [open, setOpen] = useState(false);
  const [currentCategory, setCurrentCategory] = useState<string | null>(null);

  const handleGoBack = () => setCurrentCategory(null);

  const isAnyTagChecked = tags.some(tag => tag.isChecked);
  const isFormVariant = variant === 'form';

  return !editable && !isAnyTagChecked ? (
    <span className="text-primary block max-w-full min-w-0 truncate font-semibold whitespace-nowrap">
      {NO_DATA_PLACEHOLDER}
    </span>
  ) : (
    <Popover modal open={open} {...(editable && { onOpenChange: setOpen })}>
      <PopoverTrigger asChild disabled={disabled} {...PopoverTriggerProps}>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'flex w-full max-w-full min-w-0 justify-between gap-1',
            className,
            isFormVariant
              ? cn(
                  'border-border-soft hover:border-primary text-primary h-10 rounded-sm border-[1.5px] bg-transparent px-3 py-2 font-normal shadow-none',
                  'focus-visible:border-primary hover:bg-transparent focus-visible:ring-0',
                  !isAnyTagChecked && 'text-muted-foreground'
                )
              : cn(
                  'px-2.5 py-2',
                  isAnyTagChecked
                    ? 'bg-info-soft border-0'
                    : 'bg-muted/30 font-bold',
                  !editable && isAnyTagChecked && 'hover:bg-info-soft'
                ),
            !editable && 'cursor-default'
          )}
          {...(triggerDataTestId && { 'data-testid': triggerDataTestId })}
        >
          {typeof tagLabel === 'string' ? (
            <span className="min-w-0 flex-1 truncate text-left">
              {tagLabel}
            </span>
          ) : (
            <div className="flex max-w-full min-w-0 flex-1 items-center gap-1 truncate">
              <span className="min-w-0 truncate">{tagLabel[0]},</span>
              <span className="shrink-0">+{tagLabel[1]}</span>
            </div>
          )}
          {editable && <ChevronDown className="size-4 shrink-0" />}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="flex max-h-75 w-(--radix-popover-trigger-width) flex-col p-0">
        <Command className="flex h-full flex-col">
          <CommandList className="grow overflow-auto" ref={commandListRef}>
            <CommandGroup>
              {currentCategory === DocumentTagCategory.Predefined ? (
                <>
                  <CommandItem
                    value={TagSelectorAction.Back}
                    onSelect={handleGoBack}
                    className="border-border flex items-center gap-2 rounded-none border-b px-4 py-2"
                  >
                    <ChevronDown className="size-4 rotate-90" />
                    <span>{t('back')}</span>
                  </CommandItem>
                  {predefinedTags.map(tag => (
                    <TagItem
                      key={`tag-selector-${tag.uuid}`}
                      uuid={tag.uuid}
                      name={tag.name}
                      isChecked={tag.isChecked}
                      onToggle={() => toggleTag(tag.uuid)}
                    />
                  ))}
                </>
              ) : (
                <>
                  {predefinedTags.length > 0 && (
                    <CommandItem
                      value={DocumentTagCategory.Predefined}
                      onSelect={() =>
                        setCurrentCategory(DocumentTagCategory.Predefined)
                      }
                      className="border-border flex items-center justify-between gap-2 rounded-none border-b px-4 py-2"
                    >
                      <span className="text-primary">
                        {t('components.tagSelector.categories.predefined')}
                      </span>
                      <ChevronDown className="size-4 rotate-270" />
                    </CommandItem>
                  )}
                  {customTags.length > 0 && (
                    <>
                      {customTags.map(tag => (
                        <TagItem
                          key={`tag-selector-${tag.uuid}`}
                          uuid={tag.uuid}
                          name={tag.name}
                          isChecked={tag.isChecked}
                          onToggle={() => toggleTag(tag.uuid)}
                        />
                      ))}
                    </>
                  )}
                  {customTags.length === 0 && predefinedTags.length === 0 && (
                    <div className="text-muted-foreground flex flex-col items-center justify-center p-2 text-center text-sm">
                      <p className="font-bold">
                        {t('components.tagSelector.noTags.title')}
                      </p>
                      <p>{t('components.tagSelector.noTags.message')}</p>
                    </div>
                  )}
                </>
              )}
            </CommandGroup>
          </CommandList>

          <div className="bg-border my-1 h-px" />

          {currentCategory !== DocumentTagCategory.Predefined && (
            <div className="bg-popover border-border sticky bottom-0">
              <div className="text-primary p-2 text-sm font-medium">
                {t('components.tagSelector.addTag')}
              </div>
              <div className="px-2 pb-2">
                <div className="flex min-w-0 items-center gap-1">
                  <div className="min-w-0 flex-1">
                    <Input
                      fullWidth
                      value={newTagName}
                      disabled={createTagPending}
                      onChange={handleTagNameChange}
                      onKeyDown={handleKeyDown}
                      placeholder={t(
                        'components.tagSelector.addTagPlaceholder'
                      )}
                      className={cn(
                        'h-8',
                        createTagPending && 'pointer-events-none'
                      )}
                    />
                  </div>
                  <Button
                    size="icon"
                    type="button"
                    variant="ghost"
                    className="size-8"
                    onClick={addNewTag}
                    disabled={newTagName.trim() === '' || tagError !== null}
                    {...(addTagButtonDataTestId && {
                      'data-testid': addTagButtonDataTestId,
                    })}
                  >
                    {createTagPending ? (
                      <Loader />
                    ) : (
                      <PlusCircle className="size-4" />
                    )}
                  </Button>
                </div>
              </div>
              {tagError === tagErrorEnum.tagExists && (
                <p className="text-danger-strong px-2 pb-2 text-xs">
                  {t('components.tagSelector.tagExists.title')}
                </p>
              )}
              {tagError === tagErrorEnum.tagChars && (
                <p className="text-danger-strong px-2 pb-2 text-xs">
                  {t('components.tagSelector.tagChars.title')}
                </p>
              )}
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
};
